import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/focus", label: "Areas of Focus" },
  { to: "/initiatives", label: "Initiatives" },
  { to: "/partners", label: "Who We Work With" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ra-bone/95 backdrop-blur border-b border-ra-line" : "bg-ra-bone"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-4 group"
          aria-label="Restoration Australia — Home"
        >
          <div
            className="transition-transform duration-500 group-hover:scale-[1.02]"
          >
            <img
              src="/restoration-logo.png"
              alt="Restoration Australia"
              className="ra-logo-img"
            />
          </div>
          <div className="hidden sm:block leading-none border-l border-ra-line pl-4">
            <div className="text-[0.62rem] uppercase tracking-[0.28em] text-ra-soft">
              Building the systems
            </div>
            <div className="text-[0.62rem] uppercase tracking-[0.28em] text-ra-soft mt-0.5">
              behind restoration.
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[0.78rem] uppercase tracking-[0.18em] font-medium transition-colors duration-200 pb-1 border-b ${
                  isActive
                    ? "text-ra-ochre border-ra-ochre"
                    : "text-ra-eucalypt border-transparent hover:text-ra-ochre"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 border border-ra-line"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-ra-line bg-ra-bone">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] py-2 ${
                    isActive ? "text-ra-ochre" : "text-ra-eucalypt"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
